import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  RotateCw,
  ChevronLeft,
  ChevronRight,
  Repeat,
  CheckCircle2,
  Sparkles,
  Trophy,
} from 'lucide-react';

// A 3D flip-card deck with a lightweight spaced-repetition (SM-2-flavoured)
// scheduler. config: { cards: [{ front, back }], title? }.
//
// Backward compatible: the public props (`cards`, `title`, `onComplete`) are
// unchanged. `onComplete` fires once every card has been graded at least once
// (i.e. the learner has worked the whole deck), matching the old "seen every
// card" contract but now driven by an explicit self-rating.

export interface FlashcardItem {
  front: string;
  back: string;
  // Optional richer fields — ignored if absent, so older configs still work.
  hint?: string;
  example?: string;
}

interface Props {
  cards: FlashcardItem[];
  title?: string;
  onComplete?: () => void;
}

type Rating = 'again' | 'hard' | 'good' | 'easy';

interface CardState {
  /** original index into the `cards` prop — stable identity */
  id: number;
  /** repetition count (consecutive non-"again" ratings) */
  reps: number;
  /** ease factor, SM-2 style (1.3 floor) */
  ease: number;
  /** scheduling interval, in "card turns" — smaller = sooner */
  interval: number;
  /** how many turns until this card is due again (counts down) */
  due: number;
  /** last rating, for the deck readout */
  last?: Rating;
  /** has this card ever been graded? */
  graded: boolean;
}

const RATINGS: { key: Rating; label: string; hint: string; tone: string; key2: string }[] = [
  { key: 'again', label: 'Again', hint: 'Forgot it', tone: 'again', key2: '1' },
  { key: 'hard', label: 'Hard', hint: 'Struggled', tone: 'hard', key2: '2' },
  { key: 'good', label: 'Good', hint: 'Recalled it', tone: 'good', key2: '3' },
  { key: 'easy', label: 'Easy', hint: 'Trivial', tone: 'easy', key2: '4' },
];

// Tone → token-based classes. Kept inline so light + dark both resolve via vars.
const TONE_CLASSES: Record<string, string> = {
  again: 'border-ws-danger text-ws-danger hover:bg-ws-danger/10',
  hard: 'border-ws-warning text-ws-warning hover:bg-ws-warning/10',
  good: 'border-ws-accent text-ws-accent hover:bg-ws-accent-quiet',
  easy: 'border-ws-success text-ws-success hover:bg-ws-success/10',
};

function initialState(count: number): CardState[] {
  return Array.from({ length: count }, (_, id) => ({
    id,
    reps: 0,
    ease: 2.5,
    interval: 0,
    due: id, // initial pass walks the deck in order
    graded: false,
  }));
}

// SM-2-flavoured update. Returns the next scheduling fields for a card given a
// rating. Intervals are expressed in "turns" rather than days so the deck
// reorders live within a single session.
function schedule(c: CardState, rating: Rating): Pick<CardState, 'reps' | 'ease' | 'interval' | 'due'> {
  let { reps, ease, interval } = c;
  if (rating === 'again') {
    reps = 0;
    interval = 1; // come back almost immediately
    ease = Math.max(1.3, ease - 0.2);
  } else {
    reps += 1;
    if (rating === 'hard') {
      ease = Math.max(1.3, ease - 0.15);
      interval = Math.max(2, Math.round(interval * 1.2) || 2);
    } else if (rating === 'good') {
      interval = reps === 1 ? 3 : Math.round(interval * ease) || 3;
    } else {
      // easy
      ease = ease + 0.15;
      interval = reps === 1 ? 5 : Math.round(interval * ease * 1.3) || 5;
    }
  }
  return { reps, ease, interval, due: interval };
}

export default function Flashcard({ cards, title, onComplete }: Props) {
  const [states, setStates] = useState<CardState[]>(() => initialState(cards.length));
  const [pos, setPos] = useState(0); // index into the live ordering
  const [flipped, setFlipped] = useState(false);
  const completedRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Reset if the deck identity changes (e.g. a new lesson's cards).
  useEffect(() => {
    setStates(initialState(cards.length));
    setPos(0);
    setFlipped(false);
    completedRef.current = false;
  }, [cards]);

  // Live ordering: due cards (lowest `due` first), then graded cards by `due`.
  // Stable tiebreak on id so the order is deterministic between renders.
  const order = useMemo(() => {
    return [...states]
      .sort((a, b) => a.due - b.due || a.id - b.id)
      .map((s) => s.id);
  }, [states]);

  const gradedCount = states.filter((s) => s.graded).length;
  const masteredCount = states.filter((s) => s.reps >= 2 && s.last !== 'again').length;
  const allGraded = cards.length > 0 && gradedCount === cards.length;

  const currentId = order[Math.min(pos, order.length - 1)] ?? 0;
  const current = cards[currentId];
  const currentState = states[currentId];

  const advance = useCallback(() => {
    setFlipped(false);
    setPos((p) => (p + 1) % Math.max(cards.length, 1));
  }, [cards.length]);

  const back = useCallback(() => {
    setFlipped(false);
    setPos((p) => (p - 1 + Math.max(cards.length, 1)) % Math.max(cards.length, 1));
  }, [cards.length]);

  const rate = useCallback(
    (rating: Rating) => {
      setStates((prev) => {
        const next = prev.map((s) => {
          if (s.id !== currentId) {
            // every other card edges one turn closer to due
            return { ...s, due: Math.max(0, s.due - 1) };
          }
          const sched = schedule(s, rating);
          return { ...s, ...sched, last: rating, graded: true };
        });
        const doneNow = next.every((s) => s.graded);
        if (doneNow && !completedRef.current) {
          completedRef.current = true;
          // defer to avoid setState-in-render warnings from the parent
          queueMicrotask(() => onComplete?.());
        }
        return next;
      });
      advance();
    },
    [currentId, advance, onComplete],
  );

  // Keyboard: Space/Enter flip, ←/→ navigate, 1–4 rate (when flipped).
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setFlipped((f) => !f);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        advance();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      } else if (flipped && ['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault();
        rate(RATINGS[Number(e.key) - 1].key);
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [flipped, advance, back, rate]);

  if (cards.length === 0) {
    return (
      <div className="rounded-md border border-ws-border bg-ws-surface p-6 text-sm text-ws-ink-3">
        No flashcards.
      </div>
    );
  }

  const pct = Math.round((gradedCount / cards.length) * 100);

  return (
    <div className="flex w-full max-w-xl flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="m-0 text-base font-semibold text-ws-ink">{title ?? 'Flashcards'}</h3>
        <span className="flex items-center gap-1.5 text-xs font-medium text-ws-ink-3">
          <Trophy size={13} className={masteredCount > 0 ? 'text-ws-warning' : 'text-ws-ink-4'} />
          {masteredCount}/{cards.length} mastered
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-ws-surface-2">
        <div
          className="h-full rounded-full bg-ws-accent transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* 3D flip card. Outer keeps perspective; inner rotates. */}
      <div
        ref={cardRef}
        role="button"
        tabIndex={0}
        aria-label={`Flashcard ${pos + 1} of ${cards.length}. ${
          flipped ? 'Showing back.' : 'Showing front.'
        } Press space to flip.`}
        aria-pressed={flipped}
        onClick={() => setFlipped((f) => !f)}
        className="group relative min-h-52 cursor-pointer select-none rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
        style={{ perspective: '1400px' }}
      >
        <div
          className="relative h-full min-h-52 w-full transition-transform duration-500"
          style={{
            transformStyle: 'preserve-3d',
            transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          {/* Front face */}
          <div
            className="absolute inset-0 flex min-h-52 flex-col items-center justify-center rounded-lg border-2 border-ws-border bg-ws-tile p-8 text-center shadow-sm transition-colors group-hover:border-ws-accent"
            style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
          >
            <span className="absolute left-3 top-3 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-ws-ink-4">
              Term
            </span>
            <span className="absolute right-3 top-3 flex items-center gap-1 text-[11px] text-ws-ink-4">
              <RotateCw size={12} /> flip
            </span>
            <p className="text-lg font-medium leading-relaxed text-ws-ink">{current.front}</p>
            {current.hint && (
              <p className="mt-3 text-xs italic text-ws-ink-3">Hint: {current.hint}</p>
            )}
          </div>

          {/* Back face (pre-rotated 180°) */}
          <div
            className="absolute inset-0 flex min-h-52 flex-col items-center justify-center rounded-lg border-2 border-ws-accent bg-ws-accent-quiet p-8 text-center shadow-sm"
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
            }}
          >
            <span className="absolute left-3 top-3 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-ws-accent-ink/70">
              Definition
            </span>
            <p className="text-base leading-relaxed text-ws-accent-ink">{current.back}</p>
            {current.example && (
              <p className="mt-3 rounded-sm bg-ws-surface/60 px-3 py-1.5 text-xs text-ws-ink-2">
                e.g. {current.example}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Self-rating row — only meaningful once the answer is visible. */}
      <div
        className={`grid grid-cols-4 gap-2 transition-opacity duration-200 ${
          flipped ? 'opacity-100' : 'pointer-events-none opacity-40'
        }`}
        aria-hidden={!flipped}
      >
        {RATINGS.map((r) => (
          <button
            key={r.key}
            type="button"
            disabled={!flipped}
            onClick={() => rate(r.key)}
            title={`${r.hint} (press ${r.key2})`}
            className={`flex flex-col items-center gap-0.5 rounded-sm border bg-ws-tile px-2 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed ${TONE_CLASSES[r.tone]}`}
          >
            <span>{r.label}</span>
            <span className="text-[10px] font-normal opacity-70">{r.hint}</span>
          </button>
        ))}
      </div>

      {/* Deck navigation + status */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={back}
          className="flex items-center gap-1 rounded-sm px-3 py-1.5 text-sm text-ws-ink-2 transition-colors hover:bg-ws-surface-2"
        >
          <ChevronLeft size={16} /> Prev
        </button>

        <span className="flex items-center gap-2 text-xs text-ws-ink-3">
          <span>
            {pos + 1} / {cards.length}
          </span>
          {currentState?.last && (
            <span className="flex items-center gap-1 text-ws-ink-4">
              <Repeat size={11} /> {currentState.last}
            </span>
          )}
          {allGraded ? (
            <span className="flex items-center gap-1 font-medium text-ws-success">
              <CheckCircle2 size={12} /> deck worked
            </span>
          ) : (
            <span className="flex items-center gap-1 text-ws-ink-4">
              <Sparkles size={11} /> {gradedCount} rated
            </span>
          )}
        </span>

        <button
          type="button"
          onClick={advance}
          className="flex items-center gap-1 rounded-sm px-3 py-1.5 text-sm text-ws-ink-2 transition-colors hover:bg-ws-surface-2"
        >
          Next <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
